import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface ProfileCardProps {
  id?: string;
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The panel every section of the profile sits in.
 *
 * One component so the eight cards cannot drift apart in padding, border or
 * heading weight — which is exactly what happens when each is styled where it is
 * written.
 */
export function ProfileCard({ id, title, description, action, children, className }: ProfileCardProps) {
  return (
    <section
      id={id}
      className={cn('profile-panel scroll-mt-28 rounded-[var(--radius-card)] p-5 sm:p-7', className)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-brand-400">
            {title}
          </h2>
          {description ? <p className="mt-2 text-sm text-surface-400">{description}</p> : null}
        </div>

        {action}
      </div>

      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * A card for a section the design calls for and no module serves yet.
 *
 * It renders the heading and an honest sentence — never a placeholder number.
 * A fabricated balance beside somebody's name is a promise the site cannot keep,
 * and the day it becomes real nobody would be able to tell which figures had
 * always been fiction.
 */
export function ComingSoonCard({
  title,
  children,
  icon,
}: {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <ProfileCard
      title={title}
      action={
        <span className="shrink-0 rounded-full bg-surface-800/80 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-surface-400">
          Yakında
        </span>
      }
    >
      <div className="flex items-center gap-4 rounded-xl border border-dashed border-surface-800 px-4 py-5">
        {icon ? <span className="shrink-0 text-surface-700">{icon}</span> : null}
        <p className="text-sm leading-relaxed text-surface-500">{children}</p>
      </div>
    </ProfileCard>
  );
}
